import React, { useState, useEffect } from 'react';
import { Sparkles, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import AIGenerationModal from '../components/ai/AIGenerationModal';
import ImageUploader from './ImageUploader';
import { useToast } from '../contexts/ToastContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ProductForm = ({ product = null, onSuccess, onCancel }) => {
  const toast = useToast();
  const [formData, setFormData] = useState({
    name: product?.name || '',
    description: product?.description || '',
    price: product?.price || 0,
    currency: product?.currency || 'USD',
    category: product?.category || '',
    tags: product?.tags?.join(', ') || '',
    stock: product?.stock || 0,
    status: product?.status || 'draft'
  });
  const [uploadedImages, setUploadedImages] = useState([]);
  const [loadingImages, setLoadingImages] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showAIModal, setShowAIModal] = useState(false);

  const workspaceId = 'ws_demo';

  /**
   * 🎯 PHASE 1: Récupération des médias existants
   * 
   * Fonction pour charger les objets média complets depuis le backend.
   * Supporte 2 formats backend :
   * - Array de media IDs → Fetch via /media/{id}
   * - Array d'URLs complètes → Utilisation directe (backward compatibility)
   * 
   * ✅ CORRECTION P0 (Sprint C.15 - L1 - Bug "NaN undefined"):
   * Normalisation robuste de TOUS les champs optionnels avec nullish coalescing (??).
   * Garantit qu'aucun champ ne sera undefined, même avec metadata partielle ou anciens documents.
   * 
   * Architecture évolutive : Prépare le futur Gestionnaire d'Images Enterprise
   */
  const fetchMediasByIds = async (mediaIdsOrUrls) => {
    if (!mediaIdsOrUrls || mediaIdsOrUrls.length === 0) return [];

    try {
      const mediaPromises = mediaIdsOrUrls.map(async (item, index) => {
        // Détecter si c'est une URL ou un ID
        const isUrl = typeof item === 'string' && (item.startsWith('http') || item.startsWith('/'));
        
        if (isUrl) {
          // Backend retourne des URLs complètes → Utilisation directe
          return {
            id: item, // Utiliser l'URL comme ID temporaire
            storage_url: item,
            preview: item,
            name: `image-${index + 1}.jpg`, // ✅ P0: Toujours une string valide
            size: 0, // ✅ P0: Toujours un number valide
            status: 'success',
            progress: 100
          };
        } else {
          // Backend retourne des IDs → Fetch via API
          const response = await fetch(
            `${API_URL}/api/v1/workspaces/${workspaceId}/media/${item}`,
            { credentials: 'include' }
          );

          if (!response.ok) {
            console.warn(`Failed to fetch media ${item}`);
            return null;
          }

          const mediaData = await response.json();
          
          // ✅ CORRECTION P0: Normalisation robuste avec ?? (nullish coalescing)
          // Garantit qu'aucun champ ne sera undefined/null
          return {
            id: mediaData.id ?? item,
            storage_url: mediaData.storage_url ?? '',
            preview: mediaData.storage_url ?? '',
            name: mediaData.filename ?? `media-${index + 1}`, // Toujours une string
            size: mediaData.file_size ?? 0, // Toujours un number (pas NaN)
            status: 'success',
            progress: 100
          };
        }
      });

      const results = await Promise.all(mediaPromises);
      return results.filter(media => media !== null);
    } catch (error) {
      console.error('Error fetching media:', error);
      toast.error('Erreur lors du chargement des images');
      return [];
    }
  };

  /**
   * 🔄 Chargement initial des images existantes (Mode Édition)
   * 
   * Au montage du composant, si un produit existe, on charge ses images.
   * La première image devient automatiquement l'image principale.
   * 
   * Support backward compatibility : URLs ou IDs
   */
  useEffect(() => {
    const loadExistingImages = async () => {
      if (!product || !product.images || product.images.length === 0) {
        return;
      }

      setLoadingImages(true);
      
      // Récupérer les médias complets (supporte URLs et IDs)
      const mediaObjects = await fetchMediasByIds(product.images);
      
      // Réorganiser pour que main_image soit en premier
      if (product.main_image && mediaObjects.length > 0) {
        // main_image peut être un ID ou une URL
        const mainIndex = mediaObjects.findIndex(m => 
          m.id === product.main_image || m.storage_url === product.main_image
        );
        if (mainIndex > 0) {
          const mainMedia = mediaObjects.splice(mainIndex, 1)[0];
          mediaObjects.unshift(mainMedia);
        }
      }

      setUploadedImages(mediaObjects);
      setLoadingImages(false);
    };

    loadExistingImages();
  }, [product?.id]); // Re-run si le produit change

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  /**
   * 🎯 PHASE 3: Synchronisation - Ajout d'image
   * 
   * Callback déclenché quand une nouvelle image est uploadée.
   * L'image s'ajoute à la liste existante.
   * 
   * Architecture évolutive : Support futur du drag & drop et galerie
   */
  const handleImageUploaded = (media) => {
    setUploadedImages(prev => [...prev, media]);
    toast.success('Image ajoutée avec succès');
  };

  /**
   * 🎯 PHASE 3: Synchronisation - Suppression d'image
   * 
   * Callback déclenché quand une image est supprimée.
   * Si c'était l'image principale, la suivante devient automatiquement principale.
   * 
   * Architecture évolutive : Support futur de la confirmation modale
   */
  const handleImageRemoved = (media) => {
    setUploadedImages(prev => prev.filter(img => img.id !== media.id));
    toast.success('Image supprimée');
  };

  /**
   * 🎯 PHASE 3: Synchronisation - Réorganisation
   * 
   * Callback pour la réorganisation drag & drop (ImageUploader utilise Reorder.Group).
   * La première image est toujours l'image principale.
   * 
   * Architecture évolutive : Base pour le futur Gestionnaire d'Images Enterprise
   */
  const handleImagesReordered = (newOrder) => {
    setUploadedImages(newOrder);
  };

  /**
   * 🎯 PHASE 4: Persistance
   * 
   * Sauvegarde du produit avec cohérence MongoDB ↔ Backend ↔ Frontend.
   * 
   * ✅ CORRECTION P0 (Sprint C.15 - L1):
   * Normalisation des données : extraction des media IDs purs depuis les objets.
   * Le backend convertira les IDs en URLs de manière uniforme (create + update).
   * 
   * Format envoyé : ["media_001", "media_002"] (toujours des IDs, jamais d'URLs)
   * Format stocké MongoDB : ["https://...", "https://..."] (conversion backend)
   * 
   * Architecture évolutive : Préparation pour synchronisation temps réel
   */
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.name || formData.price < 0) {
      toast.error('Veuillez remplir tous les champs requis');
      return;
    }

    setSaving(true);
    try {
      /**
       * 🔄 Normalisation des images
       * 
       * uploadedImages contient des objets avec structure :
       * { id: "media_123" | "https://...", storage_url: "https://...", ... }
       * 
       * Le backend attend uniquement des media IDs (strings).
       * On extrait soit l'ID MongoDB, soit on garde l'URL si c'est une image existante.
       */
      const normalizeMediaId = (mediaObj) => {
        // Si l'objet contient un vrai media ID (pas une URL)
        if (mediaObj.id && !mediaObj.id.startsWith('http')) {
          return mediaObj.id; // Media ID pur
        }
        // Si l'ID est une URL (backward compat avec anciens produits)
        // Le backend _convert_media_ids_to_urls() détectera que c'est déjà une URL
        return mediaObj.storage_url || mediaObj.id;
      };

      const normalizedImages = uploadedImages.map(normalizeMediaId);

      // Construire le payload avec les images dans le bon ordre
      const payload = {
        ...formData,
        price: parseFloat(formData.price),
        stock: parseInt(formData.stock),
        tags: formData.tags.split(',').map(t => t.trim()).filter(Boolean),
        images: normalizedImages, // IDs normalisés
        main_image: normalizedImages[0] || null  // Premier = principal
      };

      const url = product
        ? `${API_URL}/api/v1/workspaces/${workspaceId}/products/${product.id}`
        : `${API_URL}/api/v1/workspaces/${workspaceId}/products`;

      const method = product ? 'PATCH' : 'POST';

      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        const successMsg = product ? 'Produit modifié avec succès' : 'Produit créé avec succès';
        toast.success(successMsg);
        if (onSuccess) onSuccess();
      } else {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Échec de la sauvegarde');
      }
    } catch (error) {
      console.error('Failed to save product:', error);
      toast.error(error.message || 'Échec de la sauvegarde du produit');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6" data-testid="product-form">
      {/* Name */}
      <div>
        <Label htmlFor="name">Nom du produit *</Label>
        <Input
          id="name"
          name="name"
          value={formData.name}
          onChange={handleChange}
          required
          placeholder="T-Shirt Premium Cotton"
          data-testid="product-name-input"
        />
      </div>

      {/* Description */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <Label htmlFor="description">Description</Label>
          {product && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setShowAIModal(true)}
              className="gap-2"
              data-testid="open-ai-modal-button"
            >
              <span className="text-purple-600">✨</span>
              Générer avec IA
            </Button>
          )}
        </div>
        <Textarea
          id="description"
          name="description"
          value={formData.description}
          onChange={handleChange}
          rows={4}
          placeholder="Décrivez votre produit..."
          data-testid="product-description-input"
        />
      </div>

      {/* Price & Currency */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="price">Prix *</Label>
          <Input
            id="price"
            name="price"
            type="number"
            step="0.01"
            value={formData.price}
            onChange={handleChange}
            required
            min="0"
            data-testid="product-price-input"
          />
        </div>
        <div>
          <Label htmlFor="currency">Devise</Label>
          <select
            id="currency"
            name="currency"
            value={formData.currency}
            onChange={handleChange}
            className="w-full px-3 py-2 border border-slate-300 rounded-md"
            data-testid="product-currency-select"
          >
            <option value="USD">USD</option>
            <option value="EUR">EUR</option>
            <option value="GBP">GBP</option>
          </select>
        </div>
      </div>

      {/* Category & Stock */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="category">Catégorie</Label>
          <Input
            id="category"
            name="category"
            value={formData.category}
            onChange={handleChange}
            placeholder="Vêtements, Électronique..."
            data-testid="product-category-input"
          />
        </div>
        <div>
          <Label htmlFor="stock">Stock</Label>
          <Input
            id="stock"
            name="stock"
            type="number"
            value={formData.stock}
            onChange={handleChange}
            min="0"
            data-testid="product-stock-input"
          />
        </div>
      </div>

      {/* Tags */}
      <div>
        <Label htmlFor="tags">Tags (séparés par des virgules)</Label>
        <Input
          id="tags"
          name="tags"
          value={formData.tags}
          onChange={handleChange}
          placeholder="fashion, summer, cotton"
          data-testid="product-tags-input"
        />
      </div>

      {/* Status */}
      <div>
        <Label htmlFor="status">Statut</Label>
        <select
          id="status"
          name="status"
          value={formData.status}
          onChange={handleChange}
          className="w-full px-3 py-2 border border-slate-300 rounded-md"
          data-testid="product-status-select"
        >
          <option value="draft">Brouillon</option>
          <option value="active">Actif</option>
          <option value="archived">Archivé</option>
          <option value="out_of_stock">Rupture de stock</option>
        </select>
      </div>

      {/* 
        🎯 PHASE 2: Passage des images à ImageUploader
        
        Composant ImageUploader de référence NEXORA AI avec:
        - initialImages: Médias chargés depuis le backend (mode édition)
        - onUpload: Ajout de nouvelles images
        - onRemove: Suppression d'images
        - Drag & Drop natif pour réorganisation (Reorder.Group)
        
        Architecture évolutive : Base du futur Gestionnaire d'Images Enterprise
      */}
      <div>
        <Label className="text-lg font-medium mb-4 block">
          Images du produit
          {product && uploadedImages.length > 0 && (
            <span className="ml-2 text-sm text-gray-400 font-normal">
              ({uploadedImages.length} image{uploadedImages.length > 1 ? 's' : ''})
            </span>
          )}
        </Label>
        
        {loadingImages ? (
          <div className="flex items-center justify-center py-12 border-2 border-dashed border-gray-600 rounded-lg">
            <div className="flex flex-col items-center gap-3">
              <Loader2 className="w-8 h-8 text-blue-400 animate-spin" />
              <p className="text-sm text-gray-400">Chargement des images...</p>
            </div>
          </div>
        ) : (
          <ImageUploader
            maxFiles={10}
            maxFileSize={10}
            workspaceId={workspaceId}
            onUpload={handleImageUploaded}
            onRemove={handleImageRemoved}
            initialImages={uploadedImages}
          />
        )}
        
        {uploadedImages.length > 0 && (
          <p className="mt-2 text-xs text-gray-400">
            💡 La première image est automatiquement définie comme image principale. 
            Glissez-déposez pour réorganiser.
          </p>
        )}
      </div>

      {/* Actions */}
      <div className="flex gap-3 pt-4 border-t">
        <Button
          type="submit"
          disabled={saving}
          className="flex-1"
          data-testid="save-product-btn"
        >
          {saving ? 'Sauvegarde...' : product ? 'Modifier' : 'Créer'}
        </Button>
        {onCancel && (
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={saving}
            data-testid="cancel-product-btn"
          >
            Annuler
          </Button>
        )}
      </div>

      {/* AI Generation Modal */}
      {product && (
        <AIGenerationModal
          isOpen={showAIModal}
          onClose={() => setShowAIModal(false)}
          product={product}
          workspaceId={workspaceId}
          onGenerated={(variant) => {
            // Update description with generated content
            setFormData(prev => ({
              ...prev,
              description: variant.text,
              tags: variant.hashtags.join(', ')
            }));
          }}
        />
      )}
    </form>
  );
};

export default ProductForm;
